/**
 * @oshon-ai/primitives — component manifest contract.
 *
 * Ported verbatim from MCP_SCHEMA.md §6. Every shipping component in
 * `@oshon-ai/primitives` and `@oshon-ai/components` declares a manifest
 * validated against `ComponentManifestSchema` at test time; downstream
 * tooling (MCP server, Figma Code Connect, docs generator, Skills bundle)
 * consumes the same schema to render the component without hand-rolled
 * metadata per surface.
 *
 * This is principle #8 ("manifest-driven") and principle #9 ("trust-
 * boundary Zod") from the Oshon engineering principles.
 */

import { z } from 'zod';

/** Six density regimes defined by ARCHITECTURE.md §8 principle #1. */
export const DENSITY_REGIMES = [
  'card',
  'standard',
  'paginated',
  'virtualized',
  'server',
  'infinite',
] as const;

export const ComponentManifestSchema = z.object({
  name: z.string(),
  package: z.string(),
  version: z.string(),
  license: z.enum(['MIT', 'commercial']),
  purpose: z.string().min(10).max(280),
  dataShape: z
    .object({
      primary: z.string(),
      secondary: z.string().optional(),
    })
    .optional(),
  densitySupport: z.array(z.enum(DENSITY_REGIMES)).optional(),
  permissionsRequired: z.array(z.string()).default([]),
  permissionsOptional: z.array(z.string()).default([]),
  auditEvents: z.array(z.string()).default([]),
  a11y: z.object({
    wcagLevel: z.string(),
    keyboardMap: z.string(),
    screenReadersTested: z.array(z.string()),
    lastAudit: z.string(),
  }),
  variants: z
    .array(
      z.object({
        name: z.string(),
        description: z.string(),
      }),
    )
    .default([]),
  slots: z
    .array(
      z.object({
        name: z.string(),
        description: z.string(),
      }),
    )
    .default([]),
  doExamples: z
    .array(
      z.object({
        label: z.string(),
        code: z.string(),
      }),
    )
    .default([]),
  dontExamples: z
    .array(
      z.object({
        label: z.string(),
        code: z.string(),
        why: z.string(),
      }),
    )
    .default([]),
  figmaNode: z.string().optional(),
  designRationale: z.string(),
  relatedComponents: z.array(z.string()).default([]),
  breakingChanges: z
    .array(
      z.object({
        version: z.string(),
        change: z.string(),
        migration: z.string(),
      }),
    )
    .default([]),
});

export type ComponentManifest = z.infer<typeof ComponentManifestSchema>;
export type ComponentManifestInput = z.input<typeof ComponentManifestSchema>;

/**
 * Validate a manifest and throw a descriptive error if it fails. Designed
 * for `scripts/validate-manifests.mjs` and per-primitive tests — failures
 * carry the full Zod issue list so the author knows exactly what's wrong.
 */
export function assertValidManifest(
  manifest: unknown,
  context: string,
): ComponentManifest {
  const result = ComponentManifestSchema.safeParse(manifest);
  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    throw new Error(
      `Invalid ComponentManifest (${context}):\n${issues}`,
    );
  }
  return result.data;
}
